<script>
  import { FileText, Link as LinkIcon, MessageSquareShare, Mic, PlayCircle, Sticker } from 'lucide-svelte';
  import { conversations } from '$lib/websocket-client';
  import { getMediaUrlHints, isImage, isPDF, isVideo, preferInlineMediaUrl } from '$lib/utils';
  import VoiceMessage from './messages/VoiceMessage.svelte';
  import MediaImagePreview from './previews/MediaImagePreview.svelte';

  export let payload;
  export let isMine = false;

  function parseMaybeJson(value) {
    if (!value || typeof value !== 'string') return value;

    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  function pickSharedMessage(value) {
    if (!value || typeof value !== 'object') return value;
    if (Array.isArray(value.messages) && value.messages.length) return value.messages[0];
    return value;
  }

  function normalizePayload(value) {
    if (!value) return null;
    if (typeof value === 'string') {
      const parsed = parseMaybeJson(value);
      if (parsed && typeof parsed === 'object') return normalizePayload(parsed);

      return {
        message: value,
        sharedType: 'text',
        mime: '',
        name: '',
        size: '',
        note: null,
        fromConversationId: null
      };
    }

    const shared = pickSharedMessage(value);
    const rawMessage = shared.message || shared.url || shared.content || '';
    const parsedMessage = parseMaybeJson(rawMessage);
    const message =
      parsedMessage && typeof parsedMessage === 'object'
        ? parsedMessage.message || parsedMessage.url || parsedMessage.content || rawMessage
        : rawMessage;
    const hints = getMediaUrlHints(message);
    const mime = shared.mime || parsedMessage?.mime || hints.mime || '';
    const url = preferInlineMediaUrl(message, mime);

    return {
      message: url || message,
      sharedType: shared.sharedType || shared.type || value.sharedType || 'text',
      mime,
      name: shared.name || parsedMessage?.name || hints.name || url?.split('/').pop() || '',
      size: shared.size || shared.fileSize || shared.file_size || parsedMessage?.size || parsedMessage?.fileSize || '',
      note: value.note || null,
      fromConversationId: value.fromConversationId || null
    };
  }

  function getPreviewKind(data) {
    const type = data?.sharedType || 'text';
    const message = data?.message || '';
    const mime = data?.mime || '';

    if (type === 'sticker') return 'sticker';
    if (type === 'audio') return 'audio';
    if (isImage(message, mime)) return 'image';
    if (isVideo(message, mime)) return 'video';
    if (isPDF(message, mime)) return 'pdf';
    if (/^https?:\/\//i.test(message)) return 'link';
    if (type === 'media') return 'file';
    return 'text';
  }

  function getTitle(kind) {
    if (kind === 'image') return 'Ảnh được chia sẻ';
    if (kind === 'video') return 'Video được chia sẻ';
    if (kind === 'pdf') return 'PDF được chia sẻ';
    if (kind === 'file') return 'Tệp được chia sẻ';
    if (kind === 'link') return 'Liên kết được chia sẻ';
    if (kind === 'sticker') return 'Sticker được chia sẻ';
    if (kind === 'audio') return 'Tin nhắn thoại được chia sẻ';
    return 'Tin nhắn được chia sẻ';
  }

  function getFileName(path = '') {
    try {
      const parsed = new URL(path, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
      return decodeURIComponent(parsed.searchParams.get('name') || parsed.pathname.split('/').pop() || 'Tệp đính kèm');
    } catch {
      return path.split('/').pop() || 'Tệp đính kèm';
    }
  }

  $: data = normalizePayload(payload);
  $: previewKind = getPreviewKind(data);
  $: fromConversation = $conversations.find((item) => item.conversationId === data?.fromConversationId);
</script>

{#if data}
  <article class:is-mine={isMine} class="share-bubble">
    <div class="share-label">
      <MessageSquareShare size={13} />
      <span>Từ {fromConversation?.title || 'cuộc trò chuyện khác'}</span>
    </div>

    <div class="share-preview">
      {#if previewKind === 'image'}
        <MediaImagePreview
          src={preferInlineMediaUrl(data.message, data?.mime || '')}
          alt={data.name || 'Ảnh được chia sẻ'} />
      {:else if previewKind === 'video'}
        <div class="media-placeholder">
          <PlayCircle size={18} />
          <span>{data.name || getFileName(data.message)}</span>
        </div>
      {:else if previewKind === 'pdf'}
        <div class="media-placeholder">
          <FileText size={18} />
          <span>{data.name || getFileName(data.message)}</span>
        </div>
      {:else if previewKind === 'file'}
        <div class="media-placeholder">
          <FileText size={18} />
          <span>{data.name || getFileName(data.message)}</span>
        </div>
      {:else if previewKind === 'link'}
        <div class="media-placeholder">
          <LinkIcon size={18} />
          <span>{data.message}</span>
        </div>
      {:else if previewKind === 'sticker'}
        <div class="media-placeholder">
          <Sticker size={18} />
          <span>Sticker</span>
        </div>
      {:else if previewKind === 'audio'}
        <div class="voice-placeholder">
          <div class="voice-label"><Mic size={14} /> Tin nhắn thoại</div>
          <VoiceMessage payload={data.message} isMine={isMine} compact={true} />
        </div>
      {:else}
        <div class="media-placeholder text">
          <MessageSquareShare size={18} />
          <span>{data.message}</span>
        </div>
      {/if}
    </div>

    <div class="share-copy">
      <strong>{getTitle(previewKind)}</strong>
      {#if previewKind === 'text'}
        <p>{data.message}</p>
      {/if}
    </div>

    {#if data.note}
      <footer class="share-note">{data.note}</footer>
    {/if}
  </article>
{/if}

<style>
  .share-bubble {
    display: grid;
    gap: 0.7rem;
    min-width: min(18rem, 72vw);
    padding: 0.9rem;
    border: 1px solid rgba(148, 163, 184, 0.24);
    border-radius: 1.2rem;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(248, 250, 252, 0.98));
  }

  .share-bubble.is-mine {
    border-color: rgba(14, 165, 233, 0.26);
    background: linear-gradient(180deg, rgba(236, 254, 255, 0.96), rgba(240, 249, 255, 0.98));
  }

  .share-label {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    color: #0284c7;
    font-size: 0.68rem;
    font-weight: 700;
  }

  .share-preview {
    overflow: hidden;
    border-radius: 1rem;
    background: rgba(241, 245, 249, 0.9);
  }

  .share-preview :global(.image-preview) {
    width: 100%;
    max-width: 100%;
    margin-top: 0;
    border-radius: inherit;
  }

  .share-preview :global(.image-preview img) {
    width: 100%;
    max-height: min(16rem, 46vh);
    object-fit: cover;
    border-radius: inherit;
  }

  .media-placeholder {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    padding: 0.85rem;
    color: #334155;
  }

  .media-placeholder span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.78rem;
  }

  .media-placeholder.text span {
    white-space: normal;
    word-break: break-word;
  }

  .voice-placeholder {
    display: grid;
    gap: 0.4rem;
    padding: 0.7rem;
  }

  .voice-label {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    color: #0284c7;
    font-size: 0.68rem;
    font-weight: 700;
  }

  .share-copy strong {
    display: block;
    color: #0f172a;
    font-size: 0.82rem;
    font-weight: 700;
  }

  .share-copy p {
    margin-top: 0.3rem;
    color: #334155;
    font-size: 0.78rem;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .share-note {
    padding-top: 0.7rem;
    border-top: 1px solid rgba(226, 232, 240, 0.95);
    color: #475569;
    font-size: 0.74rem;
    font-style: italic;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
  }
</style>
